import axios from 'axios'
import { getAuthHeader } from '../../../services/api.service'

const api = (baseUrl: string, path: string) => `${baseUrl}${path}`

export interface CreateSessionBody { documentIds?: string[]; instruction?: string }
export interface MessageBody { text?: string; documentIds?: string[] }
export interface ConfirmBody { confirmationId: string; approved: boolean; reason?: string }

export async function uploadDocuments(baseUrl: string, files: File[]): Promise<{ documentIds: string[] }> {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  const { data } = await axios.post<{ documentIds: string[]; parts: unknown[] }>(api(baseUrl, '/upload'), form)
  return { documentIds: data.documentIds }
}

export async function createSession(baseUrl: string, body: CreateSessionBody): Promise<{ sessionId: string }> {
  const { data } = await axios.post<{ sessionId: string }>(api(baseUrl, '/sessions'), body)
  return data
}

/** Returns the T1 trace id of this request: the copilot service chains its callbacks into T1 off it,
 *  so it is the id an admin searches the logs for. Null when the header is absent. */
export async function postMessage(baseUrl: string, sessionId: string, body: MessageBody): Promise<{ traceId: string | null }> {
  const { headers } = await axios.post(api(baseUrl, `/sessions/${sessionId}/messages`), body)
  return { traceId: headers['x-trace-id'] ?? null }
}

export async function postConfirm(baseUrl: string, sessionId: string, body: ConfirmBody): Promise<void> {
  await axios.post(api(baseUrl, `/sessions/${sessionId}/confirm`), body)
}

export async function postInterrupt(baseUrl: string, sessionId: string): Promise<void> {
  await axios.post(api(baseUrl, `/sessions/${sessionId}/interrupt`))
}

/** The unload-path twin of `postInterrupt`, and deliberately not a refactor of it.
 *
 *  `fetch` rather than axios for two reasons that only apply while the page is being torn down:
 *  axios has no `keepalive`, so its request dies with the document, and the global interceptor that
 *  supplies `Authorization` (App.tsx) cannot be relied on to run. The header is therefore built here,
 *  exactly as `api/sse.ts` already builds it for the stream.
 *
 *  The token is a PARAMETER, not read from localStorage here, and that is the whole point: both
 *  logout paths clear `accessToken` before navigating, and `pagehide` only fires once that
 *  navigation starts. Reading storage at call time therefore sent `Bearer ` on exactly the path
 *  this exists to cover, earning a 401 and leaving the run alive. The caller supplies a token
 *  captured while the session was still good.
 *
 *  Returns void and swallows the rejection on purpose: there is nobody left to tell, and a caught
 *  promise here is what stops an AxiosError-style object — which carries the live JWT — from ever
 *  reaching a call site that might log it. A failed interrupt simply leaves the run to the
 *  service's idle TTL.
 */
export function postInterruptKeepalive(baseUrl: string, sessionId: string, token: string): void {
  fetch(api(baseUrl, `/sessions/${sessionId}/interrupt`), {
    method: 'POST',
    keepalive: true,
    headers: getAuthHeader(token),
  }).catch(() => { /* deliberately silent — see above */ })
}
